import { Button } from '@/components/ui/button';
import { Folder, Upload } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';

interface DropZoneProps {
  onDrop: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const DropZone: React.FC<DropZoneProps> = ({
  onDrop,
  accept = '.html',
  multiple = true,
  disabled = false,
  className,
  children,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragOver to false if we're leaving the dropzone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (
      x <= rect.left ||
      x >= rect.right ||
      y <= rect.top ||
      y >= rect.bottom
    ) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || isProcessing) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setIsProcessing(true);
      try {
        await onDrop(files);
      } finally {
        setIsProcessing(false);
      }
    }
  }, [onDrop, disabled, isProcessing]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setIsProcessing(true);
      try {
        await onDrop(files);
      } finally {
        setIsProcessing(false);
        // Reset the file input so the same file can be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  }, [onDrop]);

  const handleClick = useCallback(() => {
    if (!disabled && !isProcessing) {
      fileInputRef.current?.click();
    }
  }, [disabled, isProcessing]);

  return (
    <div
      className={`
        relative border-2 border-dashed rounded-lg p-12 text-center transition-all cursor-pointer
        ${isDragOver
          ? 'border-primary bg-primary/5 shadow-lg scale-105'
          : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/25'
        }
        ${disabled || isProcessing
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:shadow-md'
        }
        ${className || ''}
      `}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isProcessing}
      />

      {children || (
        <div className="space-y-6">
          <div className="flex justify-center">
            <Upload className={`h-16 w-16 ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>

          <div className="space-y-2">
            <p className="text-xl font-medium text-foreground">
              {isProcessing
                ? 'Processing files...'
                : isDragOver
                  ? 'Drop files here'
                  : 'Drag and drop HTML files here'
              }
            </p>
            <p className="text-sm text-muted-foreground">
              {isProcessing
                ? 'Please wait while we process your files'
                : 'Supports .html files. You can also click to browse files.'
              }
            </p>
          </div>

          {!isProcessing && (
            <Button variant="outline" size="lg" className="gap-2">
              <Folder className="h-5 w-5" />
              Browse Files
            </Button>
          )}

          {isProcessing && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 